import React from 'react';
import { memo, useState, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { setRecoil } from 'recoil-nexus';
import {
  accountListStateAtom,
  modifyAccountModalAtom,
} from '../../../models/atoms/account_atoms';
import { account_controller } from '../../../controllers/account_controller';
/* eslint-disable no-useless-escape */
import { useTranslation } from '../../../contexts/TranslationContext';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Paper, Stack, TableSortLabel } from '@mui/material';
import { getComparator, Order } from '../../../utils/sorting_helpers';
import { filteredAccountsSelector } from '../../../models/selectors/account_selectors';
import { MHMTypography } from '../MHM/MHMTypography';
import { MHMSortIcon } from '../MHM/MHMSortIcon';
import { MHMLabel } from '../MHM/MHMLabel';

type AccountListKey =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone_number'
  | 'permissions';

type AccountHeader = {
  label: string;
  key: AccountListKey;
  sort: boolean;
};

const headers: AccountHeader[] = [
  { label: 'First Name', key: 'first_name', sort: true },
  { label: 'Last Name', key: 'last_name', sort: true },
  { label: 'Email', key: 'email', sort: true },
  { label: 'Phone Number', key: 'phone_number', sort: true },
  { label: 'Permissions', key: 'permissions', sort: false },
];

export const AccountListComponent = memo(function AccountListComponent(
  _: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >,
) {
  const { t } = useTranslation();
  const accountList = useRecoilValue(accountListStateAtom);
  const filteredAccountList = useRecoilValue(filteredAccountsSelector);
  const [sortBy, setSortBy] = useState<AccountListKey>('first_name');
  const [sortDirection, setSortDirection] = useState<Order>('asc');

  const sortedAccounts = filteredAccountList
    .slice()
    .sort(getComparator(sortDirection, sortBy));

  // every time accountList changes we want to sort them by first name
  useEffect(() => {
    setSortBy('first_name');
    setSortDirection('asc');
  }, [accountList]);

  function updateOrder(orderBy: AccountListKey) {
    if (sortBy !== orderBy) {
      setSortBy(orderBy);
      setSortDirection('asc');
    } else {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    }
  }

  function selectAccount(accountId: string) {
    account_controller.select_account(accountId, () =>
      setRecoil(modifyAccountModalAtom, true),
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map(header => {
              const isActive = header.sort && sortBy === header.key;
              const direction: Order = isActive ? sortDirection : 'asc';

              return (
                <TableCell
                  key={header.label}
                  align="center"
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <TableSortLabel
                    active={isActive}
                    hideSortIcon={!header.sort}
                    direction={direction}
                    IconComponent={() => (
                      <MHMSortIcon order={isActive ? direction : undefined} />
                    )}
                    onClick={() => updateOrder(header.key)}
                  >
                    <MHMTypography bold>{t(header.label)}</MHMTypography>
                  </TableSortLabel>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedAccounts.map(account => {
            return (
              <TableRow
                key={account.id}
                onClick={() => selectAccount(account.id)}
              >
                <TableCell align="center">
                  <MHMTypography bold>{account.first_name}</MHMTypography>
                </TableCell>

                <TableCell align="center">
                  <MHMTypography bold>{account.last_name}</MHMTypography>
                </TableCell>

                <TableCell align="center">
                  <MHMTypography bold>{account.email}</MHMTypography>
                </TableCell>

                <TableCell align="center">
                  <MHMTypography bold>{account.phone_number}</MHMTypography>
                </TableCell>

                <TableCell align="center">
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                  >
                    {account.permissions.manage_accounts && (
                      <MHMLabel
                        label={t('Admin')}
                        labelProps={{ medium: true }}
                      />
                    )}
                    {account.permissions.register_devices && (
                      <MHMLabel
                        label={t('Install')}
                        labelProps={{ medium: true }}
                      />
                    )}
                    {account.permissions.view_devices && (
                      <MHMLabel
                        label={t('View')}
                        labelProps={{ medium: true }}
                      />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
